import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ArrowBackRounded } from '@mui/icons-material';
import { Navigate, useNavigate } from 'react-router-dom';
import { getAnalytics, logEvent } from 'firebase/analytics';

import BottomMenu from '../components/BottomMenu';
import Loading from '../components/Loading';
import {
    DEFAULT_PLAYBACK_SKIP_BACKWARD_SECONDS,
    DEFAULT_PLAYBACK_SKIP_FORWARD_SECONDS,
    PLAYBACK_SKIP_INTERVAL_OPTIONS,
} from '../constants/local_storage_constants';
import {
    persistPlaybackSkipBackwardSeconds,
    persistPlaybackSkipForwardSeconds,
    readPlaybackSkipSettings,
    restoreDefaultPlaybackSkipSettings,
} from '../utils/playbackSettings';

const PlaybackIntervalPicker = ({
    direction,
    label,
    value,
    onChange,
}) => (
    <fieldset className='playbackSettingsGroup'>
        <legend className='playbackSettingsGroupTitle'>{label}</legend>
        <div className='playbackSettingsPresets'>
            {PLAYBACK_SKIP_INTERVAL_OPTIONS.map((seconds) => {
                const selected = value === seconds;
                return (
                    <button
                        key={seconds}
                        type='button'
                        className={[
                            'playbackSettingsPresetButton',
                            selected ? 'playbackSettingsPresetButtonSelected' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => onChange(seconds)}
                        aria-label={`Set skip ${direction} to ${seconds} seconds`}
                        aria-pressed={selected}
                    >
                        {seconds}s
                    </button>
                );
            })}
        </div>
    </fieldset>
);

export const PlaybackSettings = ({
    authenticated = null,
}) => {
    const navigate = useNavigate();
    const [settings, setSettings] = useState(readPlaybackSkipSettings);

    useEffect(() => {
        logEvent(getAnalytics(), 'screen_view', {
            firebase_screen: 'playback_settings',
        });
    }, []);

    const setBackwardSeconds = (seconds) => {
        const backwardSeconds = persistPlaybackSkipBackwardSeconds(seconds);
        setSettings((currentSettings) => ({
            ...currentSettings,
            backwardSeconds,
        }));
    };

    const setForwardSeconds = (seconds) => {
        const forwardSeconds = persistPlaybackSkipForwardSeconds(seconds);
        setSettings((currentSettings) => ({
            ...currentSettings,
            forwardSeconds,
        }));
    };

    const restoreDefaults = () => {
        setSettings(restoreDefaultPlaybackSkipSettings());
    };

    const usesDefaults = (
        settings.backwardSeconds === DEFAULT_PLAYBACK_SKIP_BACKWARD_SECONDS &&
        settings.forwardSeconds === DEFAULT_PLAYBACK_SKIP_FORWARD_SECONDS
    );

    if (authenticated === false) {
        return <Navigate to={'/signin?redirect=%2Fsettings%2Fplayback'}></Navigate>;
    }

    if (authenticated !== true) {
        return (
            <div className='container bottomPadding settingsOverviewContainer'>
                <Loading />
            </div>
        );
    }

    return (
        <div className='container settingsOverviewContainer hasBottomMenu'>
            <main className='settingsOverviewContent playbackSettingsContent'>
                <header className='settingsPreferencePageHeader'>
                    <button
                        type='button'
                        className='playbackSettingsBackButton'
                        aria-label='Back to More'
                        onClick={() => navigate('/menu')}
                    >
                        <ArrowBackRounded aria-hidden='true' />
                    </button>
                    <h1 className='settingsPreferencePageTitle'>Playback settings</h1>
                    <div className='playbackSettingsSubtitle'>
                        Choose how far the full-episode player moves on the timeline.
                    </div>
                </header>

                <div className='playbackSettingsGroups'>
                    <PlaybackIntervalPicker
                        direction='backward'
                        label='Skip backward'
                        value={settings.backwardSeconds}
                        onChange={setBackwardSeconds}
                    />
                    <PlaybackIntervalPicker
                        direction='forward'
                        label='Skip forward'
                        value={settings.forwardSeconds}
                        onChange={setForwardSeconds}
                    />
                </div>

                <button
                    type='button'
                    className='playbackSettingsRestoreButton'
                    onClick={restoreDefaults}
                    disabled={usesDefaults}
                >
                    Restore defaults
                </button>
            </main>
            <BottomMenu />
        </div>
    );
};

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
});

export default connect(mapStateToProps)(PlaybackSettings);
